import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { VIDEOCALL_QA_BASEURL } from "../../../Api/api-constant";
import { Badge, Button, Callout, Card, Field, Page, PageHeader, Select, TextInput } from "../../../ui";

/**
 * Class QA · Experiments & Settings. Try transcription / speech-detection / scoring settings on
 * part of a class without touching its report, compare runs side by side, and promote a run's
 * settings to production ("Make default", logged). Talks to artium-videocall-qa
 * (/v1/experiments*, /v1/settings/*) with the CMS token the axios interceptor adds.
 */

type Settings = Record<string, string | number | null>;
interface Track {
  speaker: string; file: string; duration_s: number; voiced_s: number; speech_s: number; music_s: number;
  threshold_db: number; silent_track: boolean; language: string | null;
}
interface Run {
  id: string; label: string | null; status: string; error: string | null; settings: Settings;
  t_start_s: number | null; t_end_s: number | null; with_scores: boolean; elapsed_s: number | null;
  created_by: string; created_at: string; audio_minutes: number | null; tracks: Track[] | null;
  result?: {
    transcript: { t_start: number; t_end: number; speaker: string; activity: string; text: string | null }[];
    scores: { parameter_key: string; score?: number | null; confidence?: number | null; summary?: string | null; error?: string }[];
  } | null;
}
interface Options {
  asr_providers: string[]; asr_models: Record<string, string[]>; asr_modes: Record<string, string[]>;
  languages: string[]; llm_providers: string[]; llm_models: Record<string, string[]>;
  defaults: Settings; overridden: string[]; sarvam_configured: boolean;
  changes: { key: string; old: unknown; new: unknown; actor: string; experiment_id: string | null; note: string | null; at: string }[];
}

const api = (path: string) => `${VIDEOCALL_QA_BASEURL}${path}`;
const errText = (e: any) => e?.response?.data?.detail || e?.message || "Request failed";
const fmtT = (s?: number | null) => {
  const v = Math.max(0, Math.round(s || 0));
  return `${Math.floor(v / 60)}:${String(v % 60).padStart(2, "0")}`;
};
const mins = (s?: number | null) => (s == null ? "–" : `${(s / 60).toFixed(1)} min`);
const settingsText = (s: Settings) => {
  const parts = Object.entries(s || {}).map(([k, v]) => `${k.replace(/_/g, " ")}: ${v === "" ? "auto" : v}`);
  return parts.length ? parts.join(" · ") : "production defaults";
};
const tone = (status: string) =>
  status === "done" ? "success" : status === "failed" ? "danger" : status === "running" || status === "queued" ? "warning" : "neutral";
const speakerColor = (sp: string) => (sp === "teacher" ? "#4b2bd6" : "#c2185b");

const VideoCallQaExperiments: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const [opt, setOpt] = useState<Options | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [classes, setClasses] = useState<{ class_schedule_id: string; instrument: string | null; call_duration_s?: number | null; status: string }[]>([]);
  const [cls, setCls] = useState(params.get("class") || "");
  const [runs, setRuns] = useState<Run[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [compare, setCompare] = useState<Run[] | null>(null);
  const [msg, setMsg] = useState<{ tone: "info" | "success" | "danger"; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  // form
  const [label, setLabel] = useState("");
  const [t0, setT0] = useState("0");
  const [t1, setT1] = useState("5");
  const [asrProvider, setAsrProvider] = useState("");
  const [asrModel, setAsrModel] = useState("");
  const [asrMode, setAsrMode] = useState("");
  const [lang, setLang] = useState("");
  const [vadMode, setVadMode] = useState<"adaptive" | "fixed">("adaptive");
  const [margin, setMargin] = useState(10);
  const [fixed, setFixed] = useState(-38);
  const [withScores, setWithScores] = useState(false);
  const [llmProvider, setLlmProvider] = useState("");
  const [llmModel, setLlmModel] = useState("");

  const loadOptions = useCallback(async () => {
    try {
      const { data } = await axios.get<Options>(api("/v1/experiments/options"));
      setOpt(data);
      setError(null);
      const d = data.defaults;
      setAsrProvider(String(d.asr_provider || ""));
      setAsrModel(String(d.asr_model || ""));
      setLang(String(d.asr_language_hint || ""));
      setLlmProvider(String(d.llm_provider || ""));
      if (d.vad_threshold_db != null) { setVadMode("fixed"); setFixed(Number(d.vad_threshold_db)); }
      setMargin(Number(d.vad_margin_db ?? 10));
    } catch (e) { setError(errText(e)); }
  }, []);

  const loadRuns = useCallback(async (key: string) => {
    if (!key) return;
    try {
      const { data } = await axios.get<Run[]>(api("/v1/experiments"), { params: { class_key: key } });
      setRuns(data);
      setSelected((s) => s.filter((id) => data.some((r) => r.id === id)));
    } catch (e) { setMsg({ tone: "danger", text: errText(e) }); }
  }, []);

  useEffect(() => {
    if (!VIDEOCALL_QA_BASEURL) return;
    loadOptions();
    axios.get(api("/v1/classes"), { params: { limit: 300 } }).then(({ data }) => setClasses(data)).catch(() => undefined);
  }, [loadOptions]);

  useEffect(() => {
    if (!cls) return;
    loadRuns(cls);
  }, [cls, loadRuns]);

  // keep polling while a run is queued/running
  useEffect(() => {
    if (!runs.some((r) => r.status === "queued" || r.status === "running")) return;
    const id = window.setTimeout(() => loadRuns(cls), 6000);
    return () => window.clearTimeout(id);
  }, [runs, cls, loadRuns]);

  const providerModels = useMemo(() => (opt?.asr_models[asrProvider] || []), [opt, asrProvider]);
  const providerModes = useMemo(() => (opt?.asr_modes[asrProvider] || []), [opt, asrProvider]);

  const buildSettings = (): Settings => {
    const d = opt!.defaults, s: Settings = {};
    if (asrProvider && asrProvider !== d.asr_provider) s.asr_provider = asrProvider;
    if (asrModel.trim() && asrModel.trim() !== d.asr_model) s.asr_model = asrModel.trim();
    if (providerModes.length && asrMode) s.asr_mode = asrMode;
    if ((lang || null) !== (d.asr_language_hint || null)) s.asr_language_hint = lang;
    if (vadMode === "fixed") s.vad_threshold_db = fixed;
    else if (margin !== Number(d.vad_margin_db)) s.vad_margin_db = margin;
    if (withScores) {
      if (llmProvider && llmProvider !== d.llm_provider) s.llm_provider = llmProvider;
      if (llmModel.trim()) s.llm_model_override = llmModel.trim();
    }
    return s;
  };

  const run = async () => {
    if (!cls) { setMsg({ tone: "danger", text: "Pick a class first." }); return; }
    setBusy(true);
    try {
      await axios.post(api("/v1/experiments"), {
        class_key: cls, label: label || null, settings: buildSettings(),
        t_start_s: t0 === "" ? null : Number(t0) * 60, t_end_s: t1 === "" ? null : Number(t1) * 60, with_scores: withScores,
      });
      setMsg({ tone: "success", text: "Queued. It runs on the QA worker after any class analyses already waiting." });
      loadRuns(cls);
    } catch (e) { setMsg({ tone: "danger", text: `Could not start: ${errText(e)}` }); }
    setBusy(false);
  };

  const makeDefault = async (r: Run) => {
    const note = window.prompt(
      `Make these the production settings for classes analysed from now on?\n\n${settingsText(r.settings)}\n\nOptional note for the change log:`,
    );
    if (note === null) return;
    try {
      const { data } = await axios.post(api(`/v1/experiments/${r.id}/make-default`), { note: note || null });
      setMsg({ tone: "success", text: `Production settings changed: ${settingsText(data.changed)}. New classes use them; use “Re-analyse” to update existing classes.` });
      loadOptions();
    } catch (e) { setMsg({ tone: "danger", text: `Not changed: ${errText(e)}` }); }
  };

  const reanalyse = async () => {
    if (!window.confirm(`Re-analyse class ${cls} with the current production settings? Its report updates when the run finishes; earlier results are kept.`)) return;
    try {
      await axios.post(api(`/v1/classes/${encodeURIComponent(cls)}/reanalyse`));
      setMsg({ tone: "success", text: `Class ${cls} queued for re-analysis with the current production settings.` });
    } catch (e) { setMsg({ tone: "danger", text: `Could not start: ${errText(e)}` }); }
  };

  const reset = async (key: string) => {
    if (!window.confirm(`Reset ${key} to the environment value? Classes analysed from now on use it.`)) return;
    try { await axios.post(api(`/v1/settings/defaults/${key}/reset`)); loadOptions(); }
    catch (e) { setMsg({ tone: "danger", text: errText(e) }); }
  };

  const openCompare = async () => {
    const full = await Promise.all(selected.map((id) => axios.get<Run>(api(`/v1/experiments/${id}`)).then((r) => r.data)));
    full.sort((a, b) => +new Date(a.created_at) - +new Date(b.created_at));
    setCompare(full);
    window.setTimeout(() => document.getElementById("qa-compare")?.scrollIntoView({ behavior: "smooth" }), 50);
  };

  if (!VIDEOCALL_QA_BASEURL) {
    return <Page><Callout tone="warning">Video Call QA is not set up on this environment (REACT_APP_VIDEOCALL_QA_BASEURL is empty).</Callout></Page>;
  }

  const d = opt?.defaults || {};
  return (
    <Page>
      <PageHeader
        eyebrow="Academics · Video Call QA"
        title="QA Experiments & Settings"
        subtitle="Try transcription, speech-detection and scoring settings on part of a class without changing its report, compare runs, and choose what production uses."
      />
      {error && <Callout tone="danger" title="Could not reach the QA service">{error}</Callout>}
      {msg && <Callout tone={msg.tone === "danger" ? "danger" : msg.tone === "success" ? "success" : "info"}>{msg.text}</Callout>}

      <Card title="Production settings" subtitle="What new class analyses use. Change them with “Make default” on a finished experiment; every change is logged.">
        {!opt ? <div className="text-muted">Loading…</div> : (
          <>
            <table className="table table-sm align-middle">
              <thead><tr><th>Setting</th><th>In use</th><th>Source</th><th /></tr></thead>
              <tbody>
                {Object.entries(d).map(([k, v]) => (
                  <tr key={k}>
                    <td className="font-monospace small">{k}</td>
                    <td className="font-monospace small">{v === null || v === "" ? "auto / none" : String(v)}</td>
                    <td>{opt.overridden.includes(k) ? <Badge tone="success">set in CMS</Badge> : <span className="text-muted small">environment</span>}</td>
                    <td className="text-end">{opt.overridden.includes(k) && <Button size="sm" variant="ghost" onClick={() => reset(k)}>Reset</Button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <details>
              <summary className="small" style={{ cursor: "pointer" }}>Change log ({opt.changes.length})</summary>
              {opt.changes.length === 0 ? <div className="text-muted small mt-2">No changes yet.</div> : (
                <table className="table table-sm mt-2">
                  <thead><tr><th>When</th><th>Who</th><th>Setting</th><th>Old → new</th><th>Note</th></tr></thead>
                  <tbody>
                    {opt.changes.map((c, i) => (
                      <tr key={i}>
                        <td className="small text-nowrap">{new Date(c.at).toLocaleString()}</td>
                        <td className="small">{c.actor}</td>
                        <td className="font-monospace small">{c.key}</td>
                        <td className="font-monospace small">{String(c.old ?? "–")} → {String(c.new ?? "–")}</td>
                        <td className="small">{c.note}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </details>
          </>
        )}
      </Card>

      <Card title="New experiment" subtitle="Runs on the QA worker and stores only its own result. Use a short time range to keep it quick and cheap.">
        {opt && (
          <div className="row g-3">
            <div className="col-md-3">
              <Field label="Class ID">{(p) => (
                <>
                  <TextInput {...p} list="qa-exp-classes" value={cls} placeholder="e.g. 594426"
                    onChange={(e) => { setCls(e.target.value.trim()); setParams(e.target.value.trim() ? { class: e.target.value.trim() } : {}); }} />
                  <datalist id="qa-exp-classes">
                    {classes.filter((c) => c.call_duration_s).map((c) => (
                      <option key={c.class_schedule_id} value={c.class_schedule_id}>{`${c.instrument || ""} · ${mins(c.call_duration_s)}`}</option>
                    ))}
                  </datalist>
                </>
              )}</Field>
            </div>
            <div className="col-md-3"><Field label="Label (optional)">{(p) => <TextInput {...p} value={label} placeholder="e.g. Sarvam codemix" onChange={(e) => setLabel(e.target.value)} />}</Field></div>
            <div className="col-md-3"><Field label="From (minute)">{(p) => <TextInput {...p} numeric value={t0} onChange={(e) => setT0(e.target.value)} />}</Field></div>
            <div className="col-md-3"><Field label="To (minute)" hint="Blank = whole class">{(p) => <TextInput {...p} numeric value={t1} onChange={(e) => setT1(e.target.value)} />}</Field></div>

            <div className="col-md-3">
              <Field label="Transcription provider" hint={asrProvider === "sarvam" && !opt.sarvam_configured ? "Sarvam key not loaded on the QA host" : undefined}>{(p) => (
                <Select {...p} value={asrProvider} options={opt.asr_providers.map((x) => ({ value: x, label: x }))}
                  onChange={(e) => { setAsrProvider(e.target.value); setAsrModel((opt.asr_models[e.target.value] || [""])[0]); setAsrMode(""); }} />
              )}</Field>
            </div>
            <div className="col-md-3">
              <Field label="Model">{(p) => (
                <>
                  <TextInput {...p} list="qa-exp-models" value={asrModel} onChange={(e) => setAsrModel(e.target.value)} />
                  <datalist id="qa-exp-models">{providerModels.map((m) => <option key={m} value={m} />)}</datalist>
                </>
              )}</Field>
            </div>
            {providerModes.length > 0 && (
              <div className="col-md-3">
                <Field label="Mode" hint="saaras:v3 only; codemix suits Hindi-English">{(p) => (
                  <Select {...p} value={asrMode} options={[{ value: "", label: "default" }, ...providerModes.map((m) => ({ value: m, label: m }))]}
                    onChange={(e) => setAsrMode(e.target.value)} />
                )}</Field>
              </div>
            )}
            <div className="col-md-3">
              <Field label="Language" hint="Auto suits mixed Hindi-English best">{(p) => (
                <Select {...p} value={lang} options={[{ value: "", label: "auto-detect" }, ...opt.languages.map((l) => ({ value: l, label: l }))]}
                  onChange={(e) => setLang(e.target.value)} />
              )}</Field>
            </div>

            <div className="col-md-3">
              <Field label="Speech detection">{(p) => (
                <Select {...p} value={vadMode} options={[{ value: "adaptive", label: "adaptive (noise floor + margin)" }, { value: "fixed", label: "fixed threshold" }]}
                  onChange={(e) => setVadMode(e.target.value as "adaptive" | "fixed")} />
              )}</Field>
            </div>
            <div className="col-md-3">
              {vadMode === "adaptive" ? (
                <Field label={`Margin above noise floor: ${margin} dB`} hint="Lower = more sensitive">{(p) => (
                  <input {...p} type="range" className="form-range" min={4} max={20} step={1} value={margin} onChange={(e) => setMargin(Number(e.target.value))} />
                )}</Field>
              ) : (
                <Field label={`Fixed threshold: ${fixed} dBFS`}>{(p) => (
                  <input {...p} type="range" className="form-range" min={-70} max={-20} step={1} value={fixed} onChange={(e) => setFixed(Number(e.target.value))} />
                )}</Field>
              )}
            </div>
            <div className="col-md-3">
              <Field label="Scoring">{(p) => (
                <div className="d-flex align-items-center gap-2" style={{ minHeight: 38 }}>
                  <input {...p} type="checkbox" checked={withScores} onChange={(e) => setWithScores(e.target.checked)} />
                  <span className="small">Also score with the LLM rubrics</span>
                </div>
              )}</Field>
            </div>
            {withScores && (
              <>
                <div className="col-md-3">
                  <Field label="Scoring provider">{(p) => (
                    <Select {...p} value={llmProvider} options={opt.llm_providers.map((x) => ({ value: x, label: x }))} onChange={(e) => setLlmProvider(e.target.value)} />
                  )}</Field>
                </div>
                <div className="col-md-3">
                  <Field label="Scoring model" hint="Blank = production default">{(p) => (
                    <>
                      <TextInput {...p} list="qa-exp-llm" value={llmModel} onChange={(e) => setLlmModel(e.target.value)} />
                      <datalist id="qa-exp-llm">{(opt.llm_models[llmProvider] || []).map((m) => <option key={m} value={m} />)}</datalist>
                    </>
                  )}</Field>
                </div>
              </>
            )}
            <div className="col-12"><Button variant="primary" loading={busy} onClick={run}>Run experiment</Button></div>
          </div>
        )}
      </Card>

      <Card
        title={`Runs${cls ? ` · class ${cls}` : ""}`}
        actions={
          <div className="d-flex gap-2">
            <Button size="sm" variant="secondary" disabled={selected.length === 0} onClick={openCompare}>Compare selected</Button>
            <Button size="sm" variant="ghost" onClick={() => loadRuns(cls)}>Refresh</Button>
            <Button size="sm" variant="secondary" disabled={!cls} onClick={reanalyse}
              title="Run this class's real report again with the current production settings">
              Re-analyse this class
            </Button>
          </div>
        }
      >
        {!cls ? <div className="text-muted">Pick a class to see its runs.</div> : runs.length === 0 ? <div className="text-muted">No runs for this class yet.</div> : (
          <div className="table-responsive">
            <table className="table table-sm align-top">
              <thead><tr><th /><th>Run</th><th>Settings</th><th>Range</th><th>Status</th><th>Per person: voice / speech / music</th><th /></tr></thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <input type="checkbox" disabled={r.status !== "done"} checked={selected.includes(r.id)}
                        onChange={(e) => setSelected((s) => (e.target.checked ? [...s, r.id] : s.filter((x) => x !== r.id)))} />
                    </td>
                    <td><b>{r.label || "untitled"}</b><div className="text-muted small">{new Date(r.created_at).toLocaleString()} · {r.created_by}</div></td>
                    <td className="font-monospace small">{settingsText(r.settings)}</td>
                    <td className="small text-nowrap">{r.t_start_s != null || r.t_end_s != null ? `${fmtT(r.t_start_s)}–${r.t_end_s != null ? fmtT(r.t_end_s) : "end"}` : "whole class"}</td>
                    <td>
                      <Badge tone={tone(r.status)}>{r.status}</Badge>
                      {r.elapsed_s != null && <div className="text-muted small">{r.elapsed_s}s · {r.audio_minutes ?? "?"} audio-min</div>}
                      {r.error && <div className="text-danger small">{r.error}</div>}
                    </td>
                    <td className="small">
                      {(r.tracks || []).map((t, i) => (
                        <div key={i}>
                          <b style={{ color: speakerColor(t.speaker) }}>{t.speaker}</b>{" "}
                          {t.silent_track ? <Badge tone="danger">silent file</Badge> : `${mins(t.voiced_s)} / ${mins(t.speech_s)} / ${mins(t.music_s)}`}
                          <span className="text-muted"> · {t.language || "?"}</span>
                        </div>
                      ))}
                    </td>
                    <td className="text-end">{r.status === "done" && <Button size="sm" variant="ghost" onClick={() => makeDefault(r)}>Make default</Button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {compare && <CompareCard runs={compare} onClose={() => setCompare(null)} />}
    </Page>
  );
};

const CompareCard: React.FC<{ runs: Run[]; onClose: () => void }> = ({ runs, onClose }) => {
  const W = 15;
  const buckets = new Map<number, any[][]>();
  runs.forEach((r, i) => (r.result?.transcript || []).forEach((row) => {
    if (!row.text) return;
    const k = Math.floor(row.t_start / W) * W;
    if (!buckets.has(k)) buckets.set(k, runs.map(() => []));
    buckets.get(k)![i].push(row);
  }));
  const keys = Array.from(buckets.keys()).sort((a, b) => a - b);
  const scoreKeys = Array.from(new Set(runs.flatMap((r) => (r.result?.scores || []).map((s) => s.parameter_key))));
  return (
    <Card title="Comparison" actions={<Button size="sm" variant="ghost" onClick={onClose}>Close</Button>}>
      <div id="qa-compare" className="table-responsive">
        <table className="table table-sm align-top">
          <thead>
            <tr><th>Measure</th>{runs.map((r) => (
              <th key={r.id}>{r.label || "untitled"}<div className="text-muted small fw-normal font-monospace">{settingsText(r.settings)}</div></th>
            ))}</tr>
          </thead>
          <tbody>
            <tr><td>Time / audio</td>{runs.map((r) => <td key={r.id}>{r.elapsed_s}s · {r.audio_minutes} audio-min</td>)}</tr>
            {["teacher", "learner"].map((sp) => (
              <tr key={sp}>
                <td><b style={{ color: speakerColor(sp) }}>{sp}</b> voice / speech / music</td>
                {runs.map((r) => (
                  <td key={r.id}>{(r.tracks || []).filter((t) => t.speaker === sp).map((t, i) => (
                    <div key={i}>{t.silent_track ? "silent file" : `${mins(t.voiced_s)} / ${mins(t.speech_s)} / ${mins(t.music_s)} · ${t.language || "?"}`}</div>
                  ))}</td>
                ))}
              </tr>
            ))}
            {scoreKeys.map((k) => (
              <tr key={k}>
                <td className="font-monospace small">{k}</td>
                {runs.map((r) => {
                  const s = (r.result?.scores || []).find((x) => x.parameter_key === k);
                  return <td key={r.id}>{!s ? "–" : s.error ? <span className="text-danger small">{s.error}</span> : (
                    <><b>{s.score ?? "–"}</b> <span className="text-muted small">conf {s.confidence ?? "–"}</span><div className="text-muted small">{s.summary}</div></>
                  )}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {keys.length === 0 ? <div className="text-muted">No transcribed speech in these runs.</div> : (
        <div style={{ display: "grid", gridTemplateColumns: `80px repeat(${runs.length}, minmax(260px, 1fr))`, border: "1px solid var(--ds-border, #e6e3f0)", borderRadius: 10, maxHeight: "70vh", overflow: "auto" }}>
          <div className="p-2 fw-semibold small bg-light" style={{ position: "sticky", top: 0 }}>Time</div>
          {runs.map((r) => <div key={r.id} className="p-2 fw-semibold small bg-light" style={{ position: "sticky", top: 0 }}>{r.label || "untitled"}</div>)}
          {keys.map((k) => (
            <React.Fragment key={k}>
              <div className="p-2 border-top font-monospace small">{fmtT(k)}</div>
              {buckets.get(k)!.map((rows, i) => (
                <div key={i} className="p-2 border-top small">
                  {rows.map((x: any, j: number) => (
                    <div key={j}><b style={{ color: speakerColor(x.speaker) }}>{x.speaker === "teacher" ? "T" : "S"}</b> {x.text}</div>
                  ))}
                </div>
              ))}
            </React.Fragment>
          ))}
        </div>
      )}
    </Card>
  );
};

export default VideoCallQaExperiments;
